import { React, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Typography,
  Box,
  TableBody,
  TableCell,
  TableRow,
  InputBase,
  Snackbar,
  Alert,
  Backdrop,
  CircularProgress,
  Link,
  Button,
  FormControl,
  MenuItem,
  Select,
  FormLabel,
} from "@mui/material";
import { EnhancedTable, stableSort, getSorting } from "../common/EnhancedTable";
import { getTimeNoUTCNoTime } from "../common/dateUtils";

const rowsPerPageOptions = [10, 30, 60, 120];

export default function LicenseList({ orgId, orgName }) {
  const navigate = useNavigate();
  const [licenses, setLicenses] = useState([]);
  const [filteredLicenses, setFilteredLicenses] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const [showError, setShowError] = useState(false);
  const [simpleErrorMsg, setSimpleErrorMsg] = useState(null);
  const [searchType, setSearchType] = useState('status');
  const [isShowingAllRecords, setIsShowingAllRecords] = useState(false);

  const handleSearchTypeChange = (event) => {
    setSearchType(event.target.value);
  };

  const resetSearch = () => {
    setSearchTerm('');
    setIsShowingAllRecords(false);
    getLicenses();
  };

  const [pagination, setPagination] = useState({
    order: 'asc',
    orderBy: 'createdAt',
    page: 0,
    rowsPerPage: rowsPerPageOptions[0],
  });

  const savePaginationState = (obj) => {
    setPagination({
      ...pagination,
      order: obj.order,
      orderBy: obj.orderBy,
      page: obj.page,
      rowsPerPage: obj.rowsPerPage,
    });
  };

  useEffect(() => {
    if (orgId) {
      getLicenses();
    }
    // eslint-disable-next-line
  }, [pagination.page, pagination.rowsPerPage, orgId]);

  const handleSearch = (e) => {
    e.preventDefault();

    if (!searchTerm) {
      getLicenses();
      setIsShowingAllRecords(false);
      return;
    }
    
    setLoading(true);
    setIsShowingAllRecords(true);
    
    const url = searchType === "ownerUsername"
      ? '/api/licenses/owner-search'
      : '/api/licenses/search';
      
    const headers = {
      "Content-Type": "application/json"
    };
    
    const params = searchType === "ownerUsername"
      ? {
          orgId: orgId,
          ownerUsername: searchTerm,
        }
      : {
          orgId: orgId,
          status: searchType === "status" ? searchTerm : null,
          ownerUsername: null,
        };

    axios({
      url: url,
      method: "GET",
      headers: headers,
      params: params
    })
      .then((response) => {
        setLicenses(response.data.content);
        setFilteredLicenses(response.data.content);
        setTotalRecords(response.data.page?.totalElements || response.data.content.length);
        setLoading(false);
      })
      .catch((error) => {
        let errorMsg = "Search failed: ";
        
        if (error.response?.data?.message) {
          console.log(error);
          if (error.response.status === 401) {
            errorMsg += "Session may have expired, please refresh the page.";
          } else if (error.response.status === 404) {
            setLicenses([]);
            setFilteredLicenses([]);
            setTotalRecords(0);
            if (error.response.data?.message && searchType === "ownerUsername") {
              setErrorMessage(error.response.data.message);
            } else {
              setErrorMessage("No licenses found matching your search criteria.");
            }
            setIsShowingAllRecords(false);
            setLoading(false);
            return;
          }
        }
        
        errorMsg += error.response.data.message;
        setSimpleErrorMsg(errorMsg);
        setShowError(true);
        setLoading(false);
      });
  };

  const getLicenses = () => {
    setIsShowingAllRecords(false);
    const url = '/api/licenses';
    const headers = {
      "Content-Type": "application/json", 
    };

    const params = {
      orgId: orgId,
      page: pagination.page,
      size: pagination.rowsPerPage,
    };

    setLoading(true);
    axios({
      url: url,
      method: "GET",
      headers: headers,
      params: params,
    })
      .then((response) => {
        setLicenses(response.data.content);
        setFilteredLicenses(response.data.content);
        setTotalRecords(response.data.page.totalElements);
        setLoading(false);
      })
      .catch((error) => {
        let errorMsg = error.message ? error.message : "Something went wrong";

        if (error.response && error.response.status === 401) {
        errorMsg += ". If this was working previously, please refresh the page.";
      }

        setSimpleErrorMsg(errorMsg);
        setErrorMessage(error.response?.data?.message || "Something went wrong");
        setShowError(true);
        setLoading(false);
      });
    };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleCloseError = () => {
    setShowError(false);
  };

  const handleSortOnlyChange = (obj) => {
    setPagination({
      ...pagination,
      order: obj.order,
      orderBy: obj.orderBy,
    });
  };

  const goToLicense = (event, id) => {
    event.preventDefault();
    navigate(`/license/${id}`);
  };

  const renderLicenseList = () => {
    const { order, orderBy, page, rowsPerPage } = pagination;
    const headCells = [
      { id: 'id', label: 'License ID' },
      { id: 'products',  label: 'Products' },
      { id: 'status', label: 'Status' },
      { id: 'acquisitionMethod', label: 'Acquisition Method' },
      { id: 'autoRenew', label: 'Auto Renew' },
      { id: 'startsAt', label: 'Start Date - End Date' },
    ];

    if ((licenses === null || licenses.length === 0) && !loading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
          }}
        >
          <Typography variant="h5" color="error">
            {errorMessage}
          </Typography>
        </Box>
      );
    }

    return (
      <div id='licensesList'>
        <EnhancedTable
          headCells={headCells}
          order={order}
          orderBy={orderBy}
          count={totalRecords}
          page={isShowingAllRecords ? 0 : page}
          rowsPerPage={isShowingAllRecords ? totalRecords : rowsPerPage}
          rowsPerPageOptions={isShowingAllRecords ? [] : rowsPerPageOptions}
          savePaginationState={isShowingAllRecords ? handleSortOnlyChange : savePaginationState}>
          <TableBody>
            {stableSort(filteredLicenses, getSorting(order, orderBy)).map((item, index) => (
              <TableRow key={item.id}>
                <TableCell sx={{ fontSize: '1.1rem' }}>
                  <Link
                    color="primary"
                    style={{ cursor: "pointer" }}
                    onClick={(event) => goToLicense(event, item.id)}
                    underline="hover"
                  >
                    {item.id}
                  </Link>
                </TableCell>
                 <TableCell sx={{ fontSize: '1.1rem', pr: 10 }}>
                {item.products && item.products.length > 0 
                  ? (item.products.length > 1 
                    ? `${item.products[0].name ? item.products[0].name : item.products[0]}...` 
                    : item.products[0].name ? item.products[0].name : item.products[0])
                  : 'N/A'
                }
              </TableCell>
                <TableCell sx={{ textAlign: 'center', verticalAlign: 'middle', fontSize: '1.1rem' }}>
                  {item.status === 'Expired' ? (
                    <Box
                      component="span"
                      sx={{
                        backgroundColor: '#f44336',
                        color: '#fff',
                        borderRadius: '8px',
                        px: 2,
                        py: 0.5,
                        fontWeight: 'bold',
                        fontSize: '0.95rem',
                        display: 'inline-block',
                        ml: -4,
                      }}
                    >
                      Expired
                    </Box>
                  ) : item.status === 'ACTIVE' ? (
                    <Box
                      component="span"
                      sx={{
                        backgroundColor: '#4caf50',
                        color: '#fff',
                        borderRadius: '8px',
                        px: 2,
                        py: 0.5,
                        fontWeight: 'bold',
                        fontSize: '0.95rem',
                        display: 'inline-block',
                        ml: -4,
                      }}
                    >
                      ACTIVE
                    </Box>
                  ) : (
                      <Box
                        component="span"
                        sx={{
                          backgroundColor: '#f44336',  // Red
                          color: '#fff',
                          borderRadius: '8px',
                          px: 2,
                          py: 0.5,
                          fontWeight: 'bold',
                          fontSize: '0.95rem',
                          display: 'inline-block',
                          ml: -4,
                        }}
                      >
                    {item.status || 'UNKNOWN'}
                  </Box>
                )}
                </TableCell>
                <TableCell sx={{ fontSize: '1.1rem' }}>{item.acquisitionMethod}</TableCell>
                <TableCell sx={{ fontSize: '1.1rem' }}>{item.autoRenew !== undefined && item.autoRenew !== null ? String(item.autoRenew) : 'N/A'}</TableCell>
                <TableCell sx={{ fontSize: '1.1rem' }}>{item.startsAt && item.endsAt ? `${getTimeNoUTCNoTime(item.startsAt)} - ${getTimeNoUTCNoTime(item.endsAt)}` : 'N/A'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </EnhancedTable>
      </div>
    );
  }

  return (
    <Box sx={{ bgcolor: "primary.contrastText", minHeight: "100vh", fontFamily: "'Segoe UI', Arial, sans-serif" }}>
      {loading && (
        <Backdrop
          open={loading}
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 1,
            color: "common.white",
          }}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      {showError && (
        <>
          <Snackbar
            open={showError}
            onClose={handleCloseError}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            sx={{
              zIndex: (theme) => theme.zIndex.drawer + 2,
            }}
          >
            <Alert
              onClose={handleCloseError}
              severity="error"
              sx={{
                width: "100%",
                fontSize: "16px",
              }}
            >
              {simpleErrorMsg}
            </Alert>
          </Snackbar>
        </>
      )}
     <Box sx={{ width: "100%", mt: 5 }}>
        <Box 
          sx={{ 
            display: "flex", 
            justifyContent: "space-between", 
            alignItems: "center",
            mb: 5,
            px: 4
          }}
        >
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 600,
              color: 'primary.main' 
            }}
          >
            Licenses for: {orgName || 'Selected Organization'}
          </Typography>
           <Box
            component="form"
            onSubmit={handleSearch}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              border: "none",
              backgroundColor: "transparent",
            }}
          >
            <FormLabel
              component="legend"
              sx={{ fontSize: '14px', color: '#333333', fontWeight: 600, ml: 0.5, mb: 1 }}
            >
              Search by
            </FormLabel>
          
            <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
              <FormControl sx={{ minWidth: 120, mr: 2 }}>
                <Select
                  value={searchType}
                  onChange={handleSearchTypeChange}
                  displayEmpty
                  variant="outlined"
                  sx={{ 
                    height: '40px',
                    borderRadius: 2,
                    fontSize: 16,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#bdbdbd',
                    }
                  }}
                >
                  <MenuItem value="status">Status</MenuItem>
                  <MenuItem value="ownerUsername">Owner Username</MenuItem>
                </Select>
              </FormControl>
              <Box
                sx={{
                  border: "1px solid #bdbdbd",
                  borderRadius: 2,
                  backgroundColor: "#fff",
                  display: "flex",
                  alignItems: "center",
                  pr: 0,
                }}
              >
                <InputBase
                  sx={{ width: 260, fontSize: 18, pl: 2, py: 0.5 }}
                  placeholder="Search"
                  inputProps={{ "aria-label": "search" }}
                  value={searchTerm || ""}
                  onChange={handleSearchChange}
                />
              </Box>
              <Button
                type="submit"
                variant="contained"
                sx={{
                  ml: 3,
                  minWidth: 100,
                  minHeight: 36,
                  backgroundColor: "#2d186c",
                  color: "#fff",
                  borderRadius: 999,
                  boxShadow: "none",
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  textTransform: 'none',
                  px: 3,
                  '&:hover': { backgroundColor: "#432c8c" },
                }}
              >
                Search
              </Button>
              <Button
                type="button"
                onClick={resetSearch}
                variant="outlined"
                sx={{
                  ml: 2,
                  minWidth: 100,
                  minHeight: 36,
                  backgroundColor: "#2d186c",
                  color: "#fff",
                  borderRadius: 999,
                  boxShadow: "none",
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  textTransform: 'none',
                  px: 3,
                  '&:hover': { 
                    backgroundColor: "#432c8c" 
                  },
                }}
              >
                Reset
            </Button>
            </Box>
          </Box>
        </Box>
        {renderLicenseList()}
      </Box>
    </Box>
  );
} 